import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { adminApi, type DashboardOverview } from '../lib/api';
import { useProductScope } from '../context/ProductScopeContext';
import { copy, formatAuthMetricLabel, formatUptime, formatYesNo } from '../lib/copy';
import { formatNumber, formatRub } from '../lib/utils';
import { ErrorState, KpiCard, LoadingState, PageHeader } from '../components/ui';

const CHART_COLORS = ['#111315', '#12805c', '#b45309'];

function getAuthCounters(metrics: { counters?: Record<string, number> } | Record<string, number>) {
  if ('counters' in metrics && metrics.counters) {
    return metrics.counters;
  }
  return metrics as Record<string, number>;
}

function KpiLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="block transition-opacity hover:opacity-80">
      {children}
    </Link>
  );
}

export function DashboardPage() {
  const scope = useProductScope();
  const queryFn =
    scope === 'app'
      ? adminApi.appDashboard
      : scope === 'studio'
        ? adminApi.studioDashboard
        : adminApi.platformDashboard;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', scope],
    queryFn,
  });

  if (isLoading) return <LoadingState label="Загрузка обзора..." />;
  if (isError || !data) {
    return <ErrorState message={copy.errors.loadDashboard} onRetry={() => refetch()} />;
  }

  const stats = (data as { stats?: Record<string, number> }).stats ?? {};
  const authMigration = (data as { auth_migration?: DashboardOverview['auth_migration'] }).auth_migration;
  const authMetrics = (data as { auth_metrics?: DashboardOverview['auth_metrics'] }).auth_metrics;

  const migrationChart = authMigration
    ? [
        { name: copy.dashboard.migrationTelegram, value: authMigration.telegramOnly },
        { name: copy.dashboard.migrationEmail, value: authMigration.emailOnly },
        { name: copy.dashboard.migrationBoth, value: authMigration.emailAndTelegram },
      ].filter((item) => item.value > 0)
    : [];

  const authCounters = authMetrics ? getAuthCounters(authMetrics) : {};

  return (
    <div>
      <PageHeader
        title={copy.dashboard.title}
        description={copy.dashboard.uptime(formatUptime((data as { uptime_s?: number }).uptime_s ?? 0))}
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {scope === 'app' ? (
          <>
            <KpiLink to={`/${scope}/agents`}>
              <KpiCard label={copy.dashboard.agents} value={formatNumber(stats.active_agents ?? 0)} />
            </KpiLink>
            <KpiCard
              label="APP подписки"
              value={formatNumber(stats.app_active_subscriptions ?? 0)}
            />
            <KpiCard label="APP MRR" value={formatRub(stats.app_mrr_rub ?? 0)} />
          </>
        ) : null}
        {scope === 'studio' ? (
          <>
            <KpiCard
              label="Studio подписки"
              value={formatNumber(stats.studio_active_subscriptions ?? 0)}
            />
            <KpiCard label="Studio workspaces" value={formatNumber(stats.studio_workspaces ?? 0)} />
            <KpiCard label="Генерации" value={formatNumber(stats.generations_count ?? 0)} />
          </>
        ) : null}
        {scope === 'platform' ? (
          <>
              <KpiLink to={`/${scope}/users`}>
              <KpiCard label={copy.dashboard.users} value={formatNumber(stats.users_count ?? 0)} />
            </KpiLink>
            <KpiLink to={`/${scope}/workspaces`}>
              <KpiCard label="Workspaces" value={formatNumber(stats.workspaces_count ?? 0)} />
            </KpiLink>
            <KpiCard label={copy.dashboard.mrr} value={formatRub(stats.mrr_rub ?? 0)} />
            <KpiCard label="Платежи" value={formatNumber(stats.payments_count ?? 0)} />
          </>
        ) : null}
        <KpiLink to={`/${scope}/usage`}>
          <KpiCard
            label={copy.dashboard.creditsToday}
            value={formatNumber(stats.credits_spent_today ?? stats.token_spend_credits ?? 0)}
          />
        </KpiLink>
      </div>

      {scope === 'platform' ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <KpiCard
            label={copy.dashboard.newUsers7d}
            value={formatNumber(stats.new_users_7d ?? 0)}
          />
          <KpiCard
            label={copy.dashboard.newUsers30d}
            value={formatNumber(stats.new_users_30d ?? 0)}
          />
          <KpiLink to={`/${scope}/billing?status=failed`}>
            <KpiCard
              label={copy.dashboard.failedPayments}
              value={formatNumber(stats.failed_payments_24h ?? 0)}
            />
          </KpiLink>
        </div>
      ) : null}

      {scope === 'platform' && authMigration ? (
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="ops-card">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold">{copy.dashboard.migrationTitle}</h2>
            <Link to={`/${scope}`} className="text-xs text-ops-muted hover:text-ops-text">
              {copy.dashboard.migrationLink}
            </Link>
          </div>
          {migrationChart.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={migrationChart}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {migrationChart.map((_, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-ops-muted">{copy.dashboard.migrationEmpty}</p>
          )}
          <p className="mt-3 text-xs text-ops-muted">
            {copy.dashboard.migrationReady}: {formatYesNo(authMigration.readyForCutoff)}
          </p>
        </div>

        <div className="ops-card">
          <h2 className="mb-4 text-sm font-semibold">{copy.dashboard.authTitle}</h2>
          <dl className="space-y-2 text-sm">
            {Object.entries(authCounters).length > 0 ? (
              Object.entries(authCounters).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-4 border-b border-ops-border/50 py-2">
                  <dt className="text-ops-muted">{formatAuthMetricLabel(key)}</dt>
                  <dd>{formatNumber(Number(value))}</dd>
                </div>
              ))
            ) : (
              <p className="text-ops-muted">{copy.dashboard.authEmpty}</p>
            )}
          </dl>
        </div>
      </div>
      ) : null}
    </div>
  );
}
